import { useCallback, useEffect, useMemo, useState } from 'react';

import {
    type AgentComparison,
    type AgentTestMode,
    type RawToolDescriptionMode,
    resolveAgentComparison,
    resolveAgentTestMode,
    resolveAgentTestModeForCreate,
} from '@apps/web/components/phoebe_chat/agent_test_mode';

export function useAgentTestMode({
    hasSelectedConversation,
    rawMode,
    comparisonRunId,
    comparisonRole,
    savedRawToolDescriptions,
    onComparisonChange,
}: {
    readonly hasSelectedConversation: boolean;
    readonly rawMode: boolean;
    readonly comparisonRunId: string | null;
    readonly comparisonRole: 'normal' | 'raw' | null;
    readonly savedRawToolDescriptions: RawToolDescriptionMode;
    readonly onComparisonChange?: (comparison: AgentComparison | null) => void;
}) {
    const [pendingMode, setPendingMode] = useState<AgentTestMode>('normal');
    const [pendingRawToolDescriptions, setPendingRawToolDescriptions] =
        useState<RawToolDescriptionMode>('stripped');
    const mode = resolveAgentTestMode({
        hasSelectedConversation,
        pendingMode,
        rawMode,
        comparisonRunId,
    });
    const comparison = useMemo(
        () => resolveAgentComparison({ comparisonRunId, comparisonRole }),
        [comparisonRole, comparisonRunId],
    );
    const rawToolDescriptions = hasSelectedConversation
        ? savedRawToolDescriptions
        : pendingRawToolDescriptions;

    useEffect(() => {
        onComparisonChange?.(comparison);
        return () => {
            onComparisonChange?.(null);
        };
    }, [comparison, onComparisonChange]);

    const resetPendingMode = useCallback(() => {
        setPendingMode('normal');
        setPendingRawToolDescriptions('stripped');
    }, []);

    const modeForCreate = useCallback(
        (eligibility: {
            readonly isPlatformAdmin: boolean;
            readonly isInternalOrganization: boolean;
            readonly isV3Enabled: boolean;
            readonly isGeneralRun: boolean;
            readonly isAgentTestingSurface: boolean;
            readonly allowParallel: boolean;
        }) =>
            resolveAgentTestModeForCreate({
                pendingMode,
                ...eligibility,
            }),
        [pendingMode],
    );

    return {
        mode,
        modeForCreate,
        rawToolDescriptions,
        resetPendingMode,
        setPendingMode,
        setPendingRawToolDescriptions,
    };
}
